import React, { useMemo, useState } from 'react'

import { debounce } from 'lodash'

import { SxProps } from '@mui/material'

import { useSliceActions } from 'redux/actions/Slices'

import { TSlice, TStandardSlice } from 'types/GlobalSlice'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import FormSection from 'components_new/molecules/FormSection'
import InputTable from 'components_new/molecules/InputTable'
import ListItemButton from 'components_new/molecules/ListItemButton'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import DeleteSliceDialog from 'components_new/organisms/dialogs/DeleteSliceDialog'

import EntitySliceRow from './EntitySliceRow'
import { useTranslation } from 'language/translator'
import { useEntity } from 'redux/hooks/Entities'

interface Props {
  edit: boolean
  id: string
  /**
   * Narrows the lists to these slices, for a caller that sent the user here to
   * configure specific ones. Creating and deleting slices is out of scope in
   * that mode, so the add button and the delete option are hidden.
   */
  configureSpecificSlices?: string[]
  defaultExpanded?: boolean
  sx?: SxProps
}

const EntitySlices = React.memo((props: Props) => {
  const { edit, id, sx, configureSpecificSlices, defaultExpanded } = props
  const { t } = useTranslation()

  const { tryCreateSlice, tryDeleteSlice, tryUpdateSlice } = useSliceActions()

  const entity = useEntity(id)

  const focusIds = configureSpecificSlices
    ? new Set(configureSpecificSlices)
    : null

  const allStandardSlices = entity?.slices.standard ?? []
  const standardSlices = focusIds
    ? allStandardSlices.filter((slice) => focusIds.has(slice.id))
    : allStandardSlices

  const allCustomSlices = entity?.slices.custom ?? []
  const customSlices = focusIds
    ? allCustomSlices.filter((slice) => focusIds.has(slice.id))
    : allCustomSlices

  const [nameDialog, setNameDialog] = useState<{
    id: string | null // null for create
    open: boolean
    value: string
  }>({ id: null, open: false, value: '' })
  const [aliasDialog, setAliasDialog] = useState<{
    slice: TStandardSlice | null
    value: string
  }>({ slice: null, value: '' })

  const currentSliceNames = useMemo(
    () =>
      new Set(
        (entity?.slices.all || [])
          .filter((item) => {
            const ignoreId = aliasDialog.slice
              ? aliasDialog.slice.id
              : nameDialog.id

            return ignoreId ? item.id !== ignoreId : true
          })
          .flatMap((item) => {
            const names = [item.name.toLowerCase()]

            if ('original_name' in item) {
              names.push(item.original_name)
            }

            return names
          })
      ),
    [entity?.slices, nameDialog.id, aliasDialog.slice]
  )

  // validate value based on dialog
  const nameError = currentSliceNames.has(
    nameDialog.open
      ? nameDialog.value.toLocaleLowerCase()
      : aliasDialog.value.toLocaleLowerCase()
  )

  const handleSaveNameDialog = () => {
    if (nameDialog.id) {
      tryUpdateSlice(nameDialog.id, { name: nameDialog.value })
    } else {
      tryCreateSlice({ entity_id: id, name: nameDialog.value })
    }

    handleCloseNameDialog()
  }

  const handleCloseNameDialog = () => {
    setNameDialog((prevState) => ({ ...prevState, open: false }))

    debounce(() => {
      setNameDialog((prevState) => ({ ...prevState, value: '' }))
    }, 300)
  }

  const handleSaveAliasDialog = () => {
    if (aliasDialog.slice) {
      tryUpdateSlice(aliasDialog.slice.id, {
        name: aliasDialog.value || null
      })
    }

    handleCloseAliasDialog()
  }

  const handleCloseAliasDialog = () => {
    setAliasDialog((prevState) => ({ ...prevState, slice: null }))

    debounce(() => {
      setAliasDialog((prevState) => ({ ...prevState, alias: '' }))
    }, 300)
  }

  const [deleteSliceDialog, setDeleteSliceDialog] = useState<{
    open: boolean
    value: TSlice | null
  }>({ open: false, value: null })

  const handleOpenDeleteSlice = (slice: TSlice) => {
    setDeleteSliceDialog({ open: true, value: slice })
  }

  const handleDeleteSlice = () => {
    const sliceId = deleteSliceDialog.value?.id

    if (sliceId) {
      tryDeleteSlice(sliceId)
    }

    handleCloseDeleteSlice()
  }

  const handleCloseDeleteSlice = () => {
    setDeleteSliceDialog({ ...deleteSliceDialog, open: false })

    debounce(() => {
      setDeleteSliceDialog({ ...deleteSliceDialog, value: null })
    }, 300)
  }

  // success
  return (
    <>
      <Box sx={sx}>
        {!focusIds || standardSlices.length > 0 ? (
          <FormSection
            helperText={t('entities', 'standard_slices_required_info')}
            importance="recommended"
            title={t('global', 'standard')}
          >
            {standardSlices.length > 0 ? (
              standardSlices.map((slice) => (
                <Box
                  key={slice.id}
                  sx={{
                    display: 'flex',
                    gap: 0.5
                  }}
                >
                  <EntitySliceRow
                    defaultExpanded={defaultExpanded}
                    edit={edit}
                    slice={slice}
                    id={id}
                  />
                  {edit && !slice.read_only ? (
                    <Box sx={{ py: 0.5 }}>
                      <OptionsMenuButton
                        MenuProps={{
                          sx: { zIndex: 1302 }
                        }}
                        options={[
                          {
                            iconName: 'Edit',
                            onClick: () =>
                              setAliasDialog({
                                slice: slice,
                                value:
                                  slice.original_name === slice.name
                                    ? ''
                                    : slice.name
                              }),
                            title: t('global', 'change_name')
                          }
                        ]}
                        size="small"
                      />
                    </Box>
                  ) : null}
                </Box>
              ))
            ) : (
              <EmptyPlaceholder
                background={true}
                title={t('entities', 'no_standard_slices')}
              />
            )}
          </FormSection>
        ) : null}

        {!focusIds || customSlices.length > 0 ? (
          <FormSection
            helperText={t('entities', 'custom_slices_info')}
            importance="optional"
            title={t('global', 'custom_plural')}
          >
            {customSlices.length > 0 ? (
              customSlices.map((slice) => (
                <Box
                  key={slice.id}
                  sx={{
                    display: 'flex',
                    gap: 0.5
                  }}
                >
                  <EntitySliceRow
                    defaultExpanded={defaultExpanded}
                    edit={edit}
                    key={slice.id}
                    slice={slice}
                    id={id}
                  />
                  {edit ? (
                    <Box sx={{ py: 0.5 }}>
                      <OptionsMenuButton
                        MenuProps={{
                          sx: { zIndex: 1302 }
                        }}
                        options={[
                          {
                            iconName: 'Edit',
                            onClick: () =>
                              setNameDialog({
                                id: slice.id,
                                open: true,
                                value: slice.name
                              }),
                            title: t('global', 'change_name')
                          },
                          ...(focusIds
                            ? []
                            : [
                                {
                                  color: 'error' as const,
                                  iconName: 'Delete' as const,
                                  onClick: () => handleOpenDeleteSlice(slice),
                                  title: t('global', 'delete')
                                }
                              ])
                        ]}
                        size="small"
                      />
                    </Box>
                  ) : null}
                </Box>
              ))
            ) : (
              <EmptyPlaceholder
                background={true}
                title={t('entities', 'no_custom_slices')}
              />
            )}
            {edit && !focusIds ? (
              <ListItemButton
                icon={{
                  name: 'Add'
                }}
                onClick={() =>
                  setNameDialog({ id: null, open: true, value: '' })
                }
                size="small"
                title={t('entities', 'add_slice')}
              />
            ) : null}
          </FormSection>
        ) : null}
      </Box>

      {/*-- dialogs --*/}
      <SimpleDialog
        actions={
          <>
            <Button onClick={handleCloseNameDialog} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button onClick={handleSaveNameDialog} disabled={nameError}>
              {t('global', nameDialog.id ? 'save' : 'create')}
            </Button>
          </>
        }
        sx={{
          zIndex: 1302 // for fullSize
        }}
        onClose={handleCloseNameDialog}
        open={nameDialog.open}
        title={
          nameDialog.id
            ? t('global', 'change_name')
            : t('entities', 'create_slice')
        }
      >
        <TextField
          autoFocus
          error={nameError}
          helperText={
            nameError ? t('entities', 'duplicate_slice_error') : undefined
          }
          value={nameDialog.value}
          placeholder={t('global', 'name')}
          onChange={(ev) =>
            setNameDialog((prevState) => {
              return {
                ...prevState,
                value: ev.target.value
              }
            })
          }
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' && !nameError) {
              ev.preventDefault()
              handleSaveNameDialog()
            }
          }}
          sx={{ mt: 1 }}
        />
      </SimpleDialog>

      <SimpleDialog
        actions={
          <>
            <Button onClick={handleCloseAliasDialog} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button onClick={handleSaveAliasDialog} disabled={nameError}>
              {t('global', 'save')}
            </Button>
          </>
        }
        onClose={handleCloseAliasDialog}
        open={!!aliasDialog.slice}
        title={t('global', 'change_name')}
        sx={{
          zIndex: 1302 // for fullSize
        }}
      >
        <Box sx={{ bgcolor: 'background.default', p: 1 }}>
          <InputTable
            edit={true}
            onChange={(row, value) =>
              setAliasDialog((prevState) => ({ ...prevState, value }))
            }
            rows={
              aliasDialog.slice
                ? [
                    {
                      id: aliasDialog.slice.id,
                      title: aliasDialog.slice.original_name,
                      input: {
                        value: aliasDialog.value,
                        error: nameError,
                        helperText: nameError
                          ? t('entities', 'duplicate_slice_error')
                          : undefined
                      }
                    }
                  ]
                : []
            }
          />
        </Box>
      </SimpleDialog>

      <DeleteSliceDialog
        handleClose={handleCloseDeleteSlice}
        handleDelete={(callback) => {
          handleDeleteSlice()
          callback()
        }}
        open={deleteSliceDialog.open}
        slice={deleteSliceDialog.value}
      />
    </>
  )
})

export default EntitySlices
